import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSignInfo } from '@saiens/shared/hooks';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { AcceptanceReportPdf } from './AcceptanceReportPdf';
import type { PaymentRequestData } from '@saiens/shared/types';
import {
  CheckCircle,
  Clock,
  FileText,
  ChevronDown,
  Star,
  ArrowLeft,
} from 'lucide-react';

const ntd = new Intl.NumberFormat('zh-TW');
const formatNTD = (n: number) => `NT$${ntd.format(n)}`;

export function AcceptanceSignPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const reportRef = useRef<HTMLDivElement>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showOrderLines, setShowOrderLines] = useState(false);
  const [reportData, setReportData] = useState<PaymentRequestData | null>(null);

  const { data, isLoading, error } = useSignInfo(token);

  const handlePrintReport = async () => {
    if (!data || pdfLoading) return;
    setPdfLoading(true);
    try {
      // Build PaymentRequestData from AcceptanceSignInfo for AcceptanceReportPdf
      const pdfData: PaymentRequestData = {
        company_name: data.company_name || '',
        acceptance_name: data.name,
        project_name: data.project_name,
        sale_order_name: data.sale_order_name || null,
        partner_name: data.partner_name,
        project_addr: data.project_addr || null,
        salesperson: data.employee_name,
        coach_name: null,
        completion_date: data.completion_date,
        completion_note: data.completion_note || null,
        photo_method_note: null,
        order_lines: data.order_lines || [],
        amount_untaxed: data.amount_untaxed || 0,
        amount_tax: data.amount_tax || 0,
        amount_total: data.amount_total || 0,
        deposit_amount: data.deposit_amount || 0,
        balance_amount: data.balance_amount || 0,
        items: data.items,
        total_score: data.total_score,
        photos: data.photos || [],
        employee_signature_uri: data.employee_signature_uri || null,
        customer_signature_uri: data.customer_signature_uri || null,
        employee_signed_date: data.employee_signed_date || null,
        customer_signed_name: data.customer_signed_name || null,
        customer_signed_date: data.customer_signed_date || null,
        bank_info: null,
        company_logo_uri: null,
      };

      setReportData(pdfData);

      // Wait for component to render
      const element = await new Promise<HTMLDivElement | null>((resolve) => {
        let attempts = 0;
        const check = () => {
          if (reportRef.current) {
            resolve(reportRef.current);
          } else if (attempts < 30) {
            attempts++;
            requestAnimationFrame(check);
          } else {
            resolve(null);
          }
        };
        requestAnimationFrame(check);
      });
      if (!element) return;

      const html2pdf = (await import('html2pdf.js')).default;
      const pdfBlob = await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(element)
        .outputPdf('blob');
      const url = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      // Silently fail
    } finally {
      setPdfLoading(false);
      setReportData(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    const msg = error instanceof Error ? error.message : '無法載入';
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-white p-6">
        <div className="text-center">
          <p className="text-lg font-medium text-[#1d1d1f]">{msg}</p>
          <p className="mt-2 text-sm text-[#86868b]">
            請確認連結是否正確，或聯繫寄送者。
          </p>
        </div>
      </div>
    );
  }

  const hasAmounts = data.amount_total !== undefined && data.amount_total > 0;
  const hasDeposit = data.deposit_amount !== undefined && data.deposit_amount > 0;

  return (
    <div className="acc-page min-h-screen bg-white pb-32 text-[#1d1d1f]">
      <style>{`
        @keyframes acc-fade-in {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: none; }
        }
        .acc-page { animation: acc-fade-in 600ms cubic-bezier(0.5, 0, 0, 0.75) both; }
        @media (prefers-reduced-motion: reduce) {
          .acc-page { animation: none; }
        }
      `}</style>

      {/* Hero — 案名 + 驗收狀態 + 總分（一屏一訊息） */}
      <header className="px-6 pb-12 pt-14 text-center">
        {data.company_name && (
          <p className="text-xs font-medium text-[#86868b]">{data.company_name}</p>
        )}
        <h1 className="mt-3 text-[28px] font-semibold leading-tight tracking-[-0.015em]">
          {data.project_name || data.name}
        </h1>
        <p className="mt-1 text-sm text-[#6e6e73]">{data.name}</p>

        <div className="mt-8 flex items-center justify-center gap-1.5">
          {data.is_signed ? (
            <>
              <CheckCircle className="h-4 w-4 text-[#1d1d1f]" aria-hidden />
              <span className="text-sm font-medium">驗收完成</span>
            </>
          ) : (
            <>
              <Clock className="h-4 w-4 text-[#86868b]" aria-hidden />
              <span className="text-sm font-medium text-[#86868b]">驗收確認中</span>
            </>
          )}
        </div>
        <p className="mt-2">
          <span className="text-5xl font-semibold tabular-nums tracking-[-0.02em]">
            {data.total_score.toFixed(1)}
          </span>
          <span className="ml-1 text-base text-[#86868b]">/ 5</span>
        </p>
        <p className="mt-1 text-xs text-[#6e6e73]">驗收平均分數</p>
      </header>

      {/* 驗收摘要 */}
      <section className="bg-[#f5f5f7] px-6 py-10">
        <div className="mx-auto max-w-[653px]">
          <h2 className="text-[13px] font-medium text-[#86868b]">驗收摘要</h2>
          <dl className="mt-4 space-y-3 text-[15px]">
            <SummaryRow label="客戶" value={data.partner_name || '—'} />
            {data.project_addr && <SummaryRow label="施工地址" value={data.project_addr} />}
            <SummaryRow label="完工日期" value={data.completion_date || '—'} />
            <SummaryRow label="填表人" value={data.employee_name || '—'} />
            {data.sale_order_name && (
              <SummaryRow label="訂單編號" value={data.sale_order_name} />
            )}
          </dl>
        </div>
      </section>

      {/* 驗收項目（星等） */}
      <section className="px-6 py-10">
        <div className="mx-auto max-w-[653px]">
          <h2 className="text-[13px] font-medium text-[#86868b]">驗收項目</h2>
          <div className="mt-4 space-y-4">
            {data.items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between gap-4">
                <span className="text-[15px] text-[#393c41]">{item.name}</span>
                {item.status === 'not_applicable' ? (
                  <span className="shrink-0 text-[13px] font-medium text-[#86868b]">未施作</span>
                ) : (
                  <div
                    className="flex shrink-0 gap-1"
                    role="img"
                    aria-label={`${item.score} / 5 分`}
                  >
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className="h-[18px] w-[18px]"
                        strokeWidth={1.5}
                        fill={s <= item.score ? '#1d1d1f' : 'none'}
                        stroke={s <= item.score ? '#1d1d1f' : '#d2d2d7'}
                        aria-hidden
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 金額資訊 — 小標籤在上、數字在下 */}
      {hasAmounts && (
        <section className="bg-[#f5f5f7] px-6 py-10">
          <div className="mx-auto max-w-[653px]">
            <h2 className="text-[13px] font-medium text-[#86868b]">金額資訊</h2>
            <div className="mt-4 grid grid-cols-2 gap-6">
              <AmountCol label="訂單總額" value={formatNTD(data.amount_total ?? 0)} />
              {hasDeposit && (
                <AmountCol
                  label="已收訂金"
                  value={`−${formatNTD(data.deposit_amount ?? 0)}`}
                  muted
                />
              )}
            </div>
            {data.balance_amount !== undefined && (
              <div className="mt-6">
                <p className="text-xs text-[#6e6e73]">尾款金額</p>
                <p className="mt-1 whitespace-nowrap text-[28px] font-semibold tabular-nums tracking-[-0.015em]">
                  {formatNTD(data.balance_amount)}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 訂單明細（摺疊） */}
      {data.order_lines && data.order_lines.length > 0 && (
        <section className="px-6 py-4">
          <div className="mx-auto max-w-[653px]">
            <button
              type="button"
              onClick={() => setShowOrderLines((prev) => !prev)}
              aria-expanded={showOrderLines}
              className="flex min-h-[44px] w-full items-center justify-between py-2 text-left"
            >
              <span className="text-[15px] font-medium text-[#0071e3]">
                訂單明細（{data.order_lines.length} 項）
              </span>
              <ChevronDown
                className={`h-5 w-5 text-[#0071e3] transition-transform duration-300 ${
                  showOrderLines ? 'rotate-180' : ''
                }`}
                aria-hidden
              />
            </button>
            {showOrderLines && (
              <ul className="mt-2 space-y-3 pb-2">
                {data.order_lines.map((line) => (
                  <li
                    key={line.id}
                    className="flex items-baseline justify-between gap-4 text-sm"
                  >
                    <span className="text-[#393c41]">{line.product_name}</span>
                    <span className="shrink-0 whitespace-nowrap tabular-nums text-[#6e6e73]">
                      {formatNTD(line.price_subtotal ?? 0)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* 完工紀錄 */}
      {data.completion_note && (
        <section className="px-6 py-10">
          <div className="mx-auto max-w-[653px]">
            <h2 className="text-[13px] font-medium text-[#86868b]">完工紀錄</h2>
            <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed text-[#393c41]">
              {data.completion_note}
            </p>
          </div>
        </section>
      )}

      {/* 完工照片 */}
      {data.photos && data.photos.length > 0 && (
        <section className="bg-[#f5f5f7] px-6 py-10">
          <div className="mx-auto max-w-[653px]">
            <h2 className="text-[13px] font-medium text-[#86868b]">完工照片</h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {data.photos.map((photo, idx) => (
                <figure key={idx}>
                  <img
                    src={photo.data_uri}
                    alt={photo.name}
                    className="aspect-square w-full rounded-2xl object-cover"
                  />
                  <figcaption className="mt-1.5 truncate text-center text-xs text-[#6e6e73]">
                    {photo.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 簽章 */}
      <section className="px-6 py-10">
        <div className="mx-auto max-w-[653px]">
          <h2 className="text-[13px] font-medium text-[#86868b]">簽章</h2>
          <div className="mt-4">
            <p className="text-sm text-[#6e6e73]">業務簽章</p>
            {data.employee_signature_uri ? (
              <div className="mt-2">
                <img
                  src={data.employee_signature_uri}
                  alt="業務簽章"
                  className="max-h-14 max-w-[160px]"
                />
                {data.employee_signed_date && (
                  <p className="mt-1 text-xs text-[#86868b]">
                    {data.employee_signed_date}
                  </p>
                )}
              </div>
            ) : (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-[#86868b]">
                <Clock className="h-4 w-4" aria-hidden /> 尚未簽章
              </p>
            )}
          </div>
        </div>
      </section>

      {/* 頁尾收束（Peak-End） */}
      <footer className="bg-[#f5f5f7] px-6 py-12 text-center">
        {data.is_signed && (
          <p className="text-[15px] font-medium">本驗收單已確認完成</p>
        )}
        <p className={`text-xs text-[#6e6e73] ${data.is_signed ? 'mt-2' : ''}`}>
          {data.company_name || 'Saiens'}・感謝您的信任與託付
        </p>
      </footer>

      {/* 固定底部列 */}
      <div className="safe-area-bottom fixed bottom-0 left-0 right-0 border-t border-[#d2d2d7] bg-white/80 p-4 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[653px] gap-3">
          <button
            onClick={handlePrintReport}
            disabled={pdfLoading}
            className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-full bg-[#0071e3] px-5 text-[15px] font-medium text-white transition-colors duration-300 hover:bg-[#0077ed] disabled:opacity-60"
          >
            <FileText className="h-4 w-4" aria-hidden />
            {pdfLoading ? '產生中…' : '列印驗收報告'}
          </button>
          {data.is_owner && (
            <button
              onClick={() => navigate(`/acceptance/${data.id}`)}
              className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-full px-5 text-[15px] font-medium text-[#0071e3] shadow-[inset_0_0_0_1px_#0071e3] transition-colors duration-300 hover:bg-[#0071e3]/5"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden /> 返回
            </button>
          )}
        </div>
      </div>

      {/* Hidden PDF render target */}
      {reportData && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <AcceptanceReportPdf ref={reportRef} data={reportData} />
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-[#86868b]">{label}</dt>
      <dd className="text-right text-[#1d1d1f]">{value}</dd>
    </div>
  );
}

function AmountCol({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-[#6e6e73]">{label}</p>
      <p
        className={`mt-1 whitespace-nowrap text-lg font-medium tabular-nums ${
          muted ? 'text-[#86868b]' : 'text-[#1d1d1f]'
        }`}
      >
        {value}
      </p>
    </div>
  );
}
